import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";

import {
  PIX_CODE_SOURCE_DYNAMIC,
  PIX_CODE_SOURCE_DYNAMIC_WITH_STATIC_FALLBACK,
  PIX_CODE_SOURCE_STATIC,
  PIX_KEY_TYPES,
  isPixConfigComplete,
  normalizePixConfig,
  pixConfigUsesManualCode,
} from "../../../../lib/payments/pixConfig";

const FIXED_LABELS = {
  copy_pix: "Copiar código PIX",
  copy_digitable_line: "Copiar linha digitável",
};

const MAX_OPEN_URL = 1;
const MAX_REPLY_LIKE = 3;
const COPY_TYPES = ["copy_pix", "copy_digitable_line"];
const OPT_OUT_TYPE = "opt_out_url";
const LINK_TYPES = ["open_url", OPT_OUT_TYPE];
const REGULAR_TYPES = ["quick_reply", ...LINK_TYPES];
const ALL_BUTTON_TYPES = ["quick_reply", "open_url", "copy_pix", "copy_digitable_line"];

const ButtonConfigModal = ({
  button,
  existingButtons = [],
  allowedButtonTypes,
  pixDefaults,
  onClose,
  onSave,
}) => {
  const visibleButtonTypes = Array.isArray(allowedButtonTypes) && allowedButtonTypes.length
    ? allowedButtonTypes
    : ALL_BUTTON_TYPES;
  const defaultType = visibleButtonTypes.includes(button.type)
    ? button.type
    : visibleButtonTypes[0] || "quick_reply";

  const [type, setType] = useState(defaultType);
  const [label, setLabel] = useState(button.label || "");
  const [url, setUrl] = useState(button.url || "");
  const [pixConfig, setPixConfig] = useState(() =>
    normalizePixConfig(button.pix_config, pixDefaults || {}),
  );

  const otherButtons = existingButtons.filter((b) => b.id !== button.id);

  const openUrlCount = otherButtons.filter((b) => b.type === "open_url").length;
  const quickReplyCount = otherButtons.filter((b) => b.type === "quick_reply").length;

  const replyLikeCount =
    quickReplyCount +
    otherButtons.filter((b) => b.type === "copy_pix").length +
    otherButtons.filter((b) => b.type === "copy_digitable_line").length;

  const hasCopyPix = otherButtons.some((b) => b.type === "copy_pix");
  const hasCopyLine = otherButtons.some((b) => b.type === "copy_digitable_line");
  const hasLinkButton = otherButtons.some((b) => LINK_TYPES.includes(b.type));
  const hasCopyButtons = otherButtons.some((b) => COPY_TYPES.includes(b.type));
  const hasRegularButtons = otherButtons.some((b) => REGULAR_TYPES.includes(b.type));

  const isCopyType = type === "copy_pix" || type === "copy_digitable_line";
  const isLinkType = LINK_TYPES.includes(type);
  const isPixCopyType = type === "copy_pix";
  const pixUsesManualCode = pixConfigUsesManualCode(pixConfig);
  const pixConfigComplete = isPixConfigComplete(pixConfig);
  const manualPixKeyLabel =
    pixConfig.code_source === PIX_CODE_SOURCE_STATIC
      ? "Chave PIX fixa"
      : "Chave PIX fixa para usar se faltar";

  useEffect(() => {
    if (isCopyType) setLabel(FIXED_LABELS[type]);
  }, [type, isCopyType]);

  useEffect(() => {
    setPixConfig((current) => normalizePixConfig(current, pixDefaults || {}));
  }, [pixDefaults]);

  const handleSave = () => {
    if (type === "copy_pix" && hasCopyPix) return;
    if (type === "copy_digitable_line" && hasCopyLine) return;
    if (isLinkType && hasLinkButton) return;

    if (isCopyType && hasRegularButtons) return;
    if (!isCopyType && hasCopyButtons) return;

    if (type === "open_url" && openUrlCount >= MAX_OPEN_URL) return;
    if (type === "quick_reply" && replyLikeCount >= MAX_REPLY_LIKE) return;

    onSave({
      ...button,
      type,
      label,
      url: type === "open_url" ? url : undefined,
      pix_config: type === "copy_pix" ? {
        ...pixConfig,
        key: pixUsesManualCode
          ? pixConfig.key || pixConfig.static_code
          : pixConfig.key,
        static_code:
          pixConfig.code_source === PIX_CODE_SOURCE_DYNAMIC
            ? ""
            : pixConfig.static_code || pixConfig.key,
      } : undefined,
    });
  };

  const saveDisabled =
    (type === "open_url" && !url) ||
    (isPixCopyType && !pixConfigComplete) ||
    (type === "copy_pix" && hasCopyPix) ||
    (type === "copy_digitable_line" && hasCopyLine) ||
    (isLinkType && hasLinkButton) ||
    (isCopyType && hasRegularButtons) ||
    (!isCopyType && hasCopyButtons) ||
    (type === "open_url" && openUrlCount >= MAX_OPEN_URL) ||
    (type === "quick_reply" && replyLikeCount >= MAX_REPLY_LIKE);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>Edição do botão</DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: 2,
          pb: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <TextField
          select
          label="Tipo"
          value={type}
          onChange={(e) => {
            const nextType = e.target.value;

            if (nextType === "copy_pix" && hasCopyPix) return;
            if (nextType === "copy_digitable_line" && hasCopyLine) return;
            if (LINK_TYPES.includes(nextType) && hasLinkButton) return;

            const nextIsCopyType = COPY_TYPES.includes(nextType);
            if (nextIsCopyType && hasRegularButtons) return;
            if (!nextIsCopyType && hasCopyButtons) return;

            if (nextType === "open_url" && openUrlCount >= MAX_OPEN_URL && type !== "open_url") return;

            if (nextType === "quick_reply" && replyLikeCount >= MAX_REPLY_LIKE && type !== "quick_reply") return;

            setType(nextType);

            if (nextType !== "open_url") setUrl("");
          }}
          sx={{ mt: 2 }}
        >
          {visibleButtonTypes.includes("quick_reply") && (
            <MenuItem
              value="quick_reply"
              disabled={
                (replyLikeCount >= MAX_REPLY_LIKE && type !== "quick_reply") ||
                hasCopyButtons
              }
            >
              Resposta rápida
            </MenuItem>
          )}

          {visibleButtonTypes.includes("open_url") && (
            <MenuItem
              value="open_url"
              disabled={
                (hasLinkButton && type !== "open_url") ||
                (openUrlCount >= MAX_OPEN_URL && type !== "open_url") ||
                hasCopyButtons
              }
            >
              Abrir página
            </MenuItem>
          )}

          {visibleButtonTypes.includes(OPT_OUT_TYPE) && (
            <MenuItem
              value={OPT_OUT_TYPE}
              disabled={hasLinkButton && type !== OPT_OUT_TYPE}
            >
              Link de descadastro
            </MenuItem>
          )}

          {visibleButtonTypes.includes("copy_pix") && (
            <MenuItem
              value="copy_pix"
              disabled={(hasCopyPix && type !== "copy_pix") || hasRegularButtons}
            >
              Copia e cola PIX
            </MenuItem>
          )}

          {visibleButtonTypes.includes("copy_digitable_line") && (
            <MenuItem
              value="copy_digitable_line"
              disabled={
                (hasCopyLine && type !== "copy_digitable_line") || hasRegularButtons
              }
            >
              Copia linha digitável
            </MenuItem>
          )}
        </TextField>

        {!isCopyType && (
          <TextField
            label="Texto do botão"
            value={label}
            inputProps={{ maxLength: 20 }}
            helperText={`${label.length}/20`}
            onChange={(e) => setLabel(e.target.value)}
          />
        )}

        {isPixCopyType && (
          <>
            <TextField
              select
              label="Origem do PIX"
              value={pixConfig.code_source}
              onChange={(event) => {
                const codeSource = event.target.value;
                setPixConfig((current) =>
                  normalizePixConfig(
                    {
                      ...current,
                      code_source: codeSource,
                      static_code:
                        current.static_code ||
                        (codeSource === PIX_CODE_SOURCE_DYNAMIC ? "" : current.key),
                    },
                    pixDefaults || {},
                  ),
                );
              }}
            >
              <MenuItem value={PIX_CODE_SOURCE_DYNAMIC}>
                Código PIX dinâmico da listagem
              </MenuItem>
              <MenuItem value={PIX_CODE_SOURCE_STATIC}>Chave PIX fixa</MenuItem>
              <MenuItem value={PIX_CODE_SOURCE_DYNAMIC_WITH_STATIC_FALLBACK}>
                Dinâmico da listagem; se faltar, usar chave fixa
              </MenuItem>
            </TextField>

            <TextField
              label="Nome exibido no PIX"
              value={pixConfig.merchant_name}
              inputProps={{ maxLength: 56 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {String(pixConfig.merchant_name || "").length}/56
                  </InputAdornment>
                ),
              }}
              onChange={(event) =>
                setPixConfig((current) => ({
                  ...current,
                  merchant_name: event.target.value,
                }))
              }
            />

            {pixUsesManualCode && (
              <>
                <TextField
                  select
                  label="Tipo da chave fixa"
                  value={pixConfig.key_type}
                  onChange={(event) =>
                    setPixConfig((current) => ({
                      ...current,
                      key_type: event.target.value,
                    }))
                  }
                >
                  {PIX_KEY_TYPES.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label={manualPixKeyLabel}
                  value={pixConfig.key}
                  helperText={
                    pixConfig.code_source === PIX_CODE_SOURCE_STATIC
                      ? "Usada em todos os envios deste modelo"
                      : "Usada apenas quando a listagem não trouxer Código_PIX"
                  }
                  onChange={(event) =>
                    setPixConfig((current) => ({
                      ...current,
                      key: event.target.value,
                      static_code: event.target.value,
                    }))
                  }
                />
              </>
            )}
          </>
        )}

        {type === "open_url" && (
          <TextField
            label="Link"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saveDisabled}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ButtonConfigModal;
